<template>
  <OnboardingLayout :show-sign-in="false">
    <div class="consents">
      <div class="consents__intro">
        <h1 class="text-h4 font-weight-medium">
          {{ $t('signup.v2.consents.title') }}
        </h1>
        <p class="text-body-1">
          {{ $t('signup.v2.consents.subtitle') }}
        </p>
      </div>

      <v-alert
        v-if="!hasConsentItems"
        type="error"
        density="compact"
        variant="tonal"
        rounded="0"
      >
        {{ $t('alerts.legal-consents.not-configured') }}
        <template #append>
          <v-btn
            variant="text"
            size="small"
            @click="reloadPage"
          >
            {{ $t('alerts.legal-consents.retry') }}
          </v-btn>
        </template>
      </v-alert>

      <v-form
        v-else
        class="consents__form"
        @submit.prevent="onContinue"
      >
        <v-checkbox
          v-for="item in consentItems"
          :key="item.identifier"
          v-model="acceptedIdentifiers"
          :value="item.identifier"
          color="primary"
          density="compact"
          hide-details
        >
          <template #label>
            <span class="text-body-1 ml-2">
              {{ labelParts(item).before
              }}<a
                v-if="item.url"
                :href="item.url"
                target="_blank"
                rel="noopener noreferrer"
                class="font-weight-bold consents__link"
                @click.stop
                >{{ item.name }}</a
              >{{ labelParts(item).after }}
              <span v-if="item.required === false"> ({{ $t('labels.optional') }})</span>
            </span>
          </template>
        </v-checkbox>

        <v-alert
          v-if="alertError"
          type="error"
          density="compact"
          variant="tonal"
          rounded="0"
        >
          {{ $t('alerts.legal-consents.error') }}
        </v-alert>

        <div class="d-flex justify-center pt-4">
          <v-btn
            type="submit"
            color="primary"
            height="44"
            rounded="xl"
            :block="xs"
            class="px-8"
            :loading="submitting"
            :disabled="!canContinue"
          >
            {{ $t('signup.v2.consents.continue') }}
          </v-btn>
        </div>
      </v-form>
    </div>
  </OnboardingLayout>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'

import featureMixin from '@/mixins/feature'
import { submitFieldConsents } from '@/modules/iubenda/submitFieldConsents'
import {
  buildConsentSubmissionFields,
  buildLegalConsentMutationInput,
} from '@/modules/legalConsents/buildConsentFields'
import { resolveConsentItemText } from '@/modules/legalConsents/resolveConsentItemText'
import type { LegalConsentItem } from '@/modules/legalConsents/types'
import ACCEPT_LEGAL_CONSENTS from '@/queries/AcceptLegalConsents.gql'
import LEGAL_CONSENTS from '@/queries/LegalConsents.gql'

import OnboardingLayout from './components/OnboardingLayout.vue'
import { useBetaOnboarding } from './composables/useBetaOnboarding.ts'

type ConsentItem = LegalConsentItem

defineOptions({ name: 'BetaLegalConsents', mixins: [featureMixin] })

const proxy = getCurrentInstance()!.proxy as any
const router = useRouter()
const onboarding = useBetaOnboarding()
const { xs } = useDisplay()

const t = (key: string, ...args: unknown[]) => proxy.$t(key, ...args)
const te = (key: string) => proxy.$te(key)

const consentItems = computed<ConsentItem[]>(() => {
  const items: ConsentItem[] =
    proxy.getFeature(proxy.featureNames.LEGAL_CONSENTS)?.config?.items || []
  return items.filter((item) => item.context?.includes('pre-onboarding'))
})

const requiredItems = computed(() => consentItems.value.filter((item) => item.required !== false))

const acceptedIdentifiers = ref<string[]>([])
const submitting = ref(false)
const alertError = ref(false)

const allRequiredAccepted = computed(() =>
  requiredItems.value.every((item) => acceptedIdentifiers.value.includes(item.identifier))
)

// No pre-onboarding items means a misconfigured feature. Submitting nothing would write no
// consent records, and the welcome route requires at least one, so the user would loop back
// here. Show a recoverable error instead of allowing an empty submission.
const hasConsentItems = computed(() => consentItems.value.length > 0)

const canContinue = computed(() => hasConsentItems.value && allRequiredAccepted.value)

function reloadPage() {
  window.location.reload()
}

function labelParts(item: ConsentItem) {
  const text: string = resolveConsentItemText(item, t, te)
  if (!item.url) return { before: text, after: '' }

  const index = text.indexOf(item.name)
  if (index === -1) return { before: `${text} `, after: '' }

  return {
    before: text.slice(0, index),
    after: text.slice(index + item.name.length),
  }
}

async function onContinue() {
  alertError.value = false
  if (!canContinue.value) return

  const acceptedItems = consentItems.value.filter((item) =>
    acceptedIdentifiers.value.includes(item.identifier)
  )
  const companyId = proxy.$auth.user.companyId

  submitting.value = true
  try {
    await proxy.$apollo.mutate({
      mutation: ACCEPT_LEGAL_CONSENTS,
      variables: {
        companyId,
        items: buildLegalConsentMutationInput(acceptedItems, t, te),
      },
      refetchQueries: [{ query: LEGAL_CONSENTS, variables: { id: companyId } }],
    })
  } catch (error) {
    alertError.value = true
    submitting.value = false
    return
  }

  submitFieldConsents(buildConsentSubmissionFields(acceptedItems, te), proxy.$t)
  proxy.$tracking.event('SignUp Account', 'Consents accepted')
  onboarding.acceptConsents()
  submitting.value = false
  await router.replace({ name: 'beta-welcome' })
}
</script>

<style scoped>
.consents {
  max-width: 510px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.consents__intro {
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
  color: rgb(var(--v-theme-on-surface));
}

.consents__link {
  text-decoration: underline;
  text-underline-position: from-font;
}

@media (max-width: 599px) {
  .consents {
    max-width: none;
    width: 100%;
    padding: 20px 12px;
    align-items: stretch;
  }

  .consents__intro {
    text-align: left;
    gap: 6px;
  }
  .consents__intro h1 {
    font-size: 22px !important;
    line-height: 28px !important;
  }
  .consents__intro p {
    font-size: 13px;
    line-height: 18px;
  }

  .consents :deep(.v-label) {
    font-size: 13px;
    line-height: 18px;
  }
}
</style>
